import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Select, Button, Card, Space, message, Switch, Row, Col, Table, Tag, Alert, Modal, Tabs, DatePicker } from 'antd';
import { TrInputNumber as InputNumber } from '../components/TrInputNumber';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftOutlined, SaveOutlined, AppstoreOutlined, DollarOutlined, UnorderedListOutlined, BarcodeOutlined, PictureOutlined, ShopOutlined, CarOutlined, HomeOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../services/api';
import { fetchCategoryAttributes, AttributeDataType, type CategoryAttributeDto } from '../services/categoryAttributeService';
import { fetchProductBrands, type ProductBrandDto } from '../services/productBrandService';
import type { ProductAttributeValueDto } from '../services/productService';
import ProductVariantSelector from '../components/ProductVariantSelector';
import ProductImageUploader from '../components/ProductImageUploader';
import ProductBarcodeManager from '../components/ProductBarcodeManager';
import SimplePriceManager from '../components/SimplePriceManager';
import ProductChannelManager from '../components/ProductChannelManager';
import VehicleCompatibilityManager from '../components/VehicleCompatibilityManager';
import { useCurrency } from '../contexts/CurrencyContext';

interface Category {
  id: number;
  categoryName: string;
  categoryCode: string;
  parentId?: number | null;
}

interface Unit {
  id: number;
  name?: string;
  unitName?: string;
  symbol?: string;
  unitCode?: string;
  isBaseUnit?: boolean;
  baseRatio?: number;
  conversionFactor?: number;
  unitSetId?: number;
}

interface GeneralSettings {
  DefaultTaxRate: string;
  DefaultUnitId: string;
  DefaultRetailMarginPercent?: string;
  DefaultWholesaleMarginPercent?: string;
}

interface Warehouse {
  id: number;
  warehouseName: string;
}

interface WarehouseStock {
  stockId?: number; // product_stocks kaydının ID'si
  warehouseId: number;
  warehouseName: string;
  quantity: number | null;
  minStockLevel: number;
  maxStockLevel: number;
  shelfCode?: string; // Raf kodu
  hasStockRecord: boolean;
}

interface ProductTypeOption {
  value: number;
  label: string;
  trackStock: boolean;
}

const PRODUCT_TYPE_OPTIONS: ProductTypeOption[] = [
  { value: 1, label: 'Malzeme (Stoklu)', trackStock: true },
  { value: 2, label: 'Hizmet', trackStock: false },
  { value: 3, label: 'Sarf Malzeme', trackStock: true },
  { value: 4, label: 'Promosyon', trackStock: false },
  { value: 5, label: 'Sabit Kıymet', trackStock: false },
];

const determineTrackStockByType = (productType: number): boolean => {
  const option = PRODUCT_TYPE_OPTIONS.find(opt => opt.value === productType);
  return option ? option.trackStock : false;
};

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [productBrands, setProductBrands] = useState<ProductBrandDto[]>([]);
  const [productBrandSelectKey, setProductBrandSelectKey] = useState(0);
  const [units, setUnits] = useState<Unit[]>([]);
  const [settings, setSettings] = useState<GeneralSettings | null>(null);
  const [productVariants, setProductVariants] = useState<any[]>([]);
  const [productBarcodes, setProductBarcodes] = useState<any[]>([]);
  const [hasVariants, setHasVariants] = useState(false);
  
  // Fiyatlandırma ayarlarını state'e al
  const [isPriceVariant, setIsPriceVariant] = useState(false);
  const [isWholesalePrice, setIsWholesalePrice] = useState(false);
  const [autoGenerateCode, setAutoGenerateCode] = useState(true);
  const [hasStockHistory, setHasStockHistory] = useState(false);
  const [productType, setProductType] = useState<number>(1);
  
  // Otomatik kar oranı ayarları
  const [useAutoMargin, setUseAutoMargin] = useState(true);
  const [retailMarginPercent, setRetailMarginPercent] = useState<number | null>(null);
  const [wholesaleMarginPercent, setWholesaleMarginPercent] = useState<number | null>(null);
  
  // Depo stok ayarları için state
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseStocks, setWarehouseStocks] = useState<WarehouseStock[]>([]);
  const [bulkMin, setBulkMin] = useState<number>(0);
  const [bulkMax, setBulkMax] = useState<number>(1000);
  const [stocksLoading, setStocksLoading] = useState(false);
  
  // Stok transfer modal state
  const [stockTransferModalVisible, setStockTransferModalVisible] = useState(false);
  const [stockTransferData, setStockTransferData] = useState<{mainProductStock?: number; variantStock?: number; variantId?: number; message?: string} | null>(null);
  const [availableVariants, setAvailableVariants] = useState<any[]>([]);
  const [selectedTargetVariantId, setSelectedTargetVariantId] = useState<number | null>(null);
  const { baseCurrencySymbol } = useCurrency();
  const enableCriticalStockAlert = Form.useWatch('enableCriticalStockAlert', form);
  const formCategoryId = Form.useWatch('categoryId', form);

  // Kategori öznitelikleri (genişlik, yükseklik, üretim yılı vb.)
  const [categoryAttributes, setCategoryAttributes] = useState<CategoryAttributeDto[]>([]);
  const [productAttributeValues, setProductAttributeValues] = useState<Array<{ categoryAttributeId: number; valueNumeric?: number; valueText?: string; valueDate?: string }>>([]);
  const skipAttributeResetRef = useRef(false);
  const savedProductIsActiveRef = useRef<boolean>(true);

  const isEdit = !!id;
  const activeTab = searchParams.get('tab') || 'basic';

  useEffect(() => {
    loadData();
    if (id) {
      loadProduct(id);
    }
  }, [id]);

  // Kategori seçildiğinde o kategorinin özniteliklerini yükle (kategori değişince değerler sıfırlanır; ürün yüklenince skip)
  useEffect(() => {
    if (!formCategoryId) {
      setCategoryAttributes([]);
      setProductAttributeValues([]);
      return;
    }
    if (skipAttributeResetRef.current) {
      skipAttributeResetRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const attrs = await fetchCategoryAttributes(formCategoryId);
        if (!cancelled) {
          setCategoryAttributes(attrs);
          setProductAttributeValues(attrs.map(a => ({ categoryAttributeId: a.id })));
        }
      } catch {
        if (!cancelled) {
          setCategoryAttributes([]);
          setProductAttributeValues([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [formCategoryId]);

  const loadData = async () => {
    try {
      console.log('🔄 Loading data...');
      const [categoriesRes, productBrandsRes, unitsRes, settingsRes] = await Promise.all([
        api.get<Category[]>('/categories'),
        fetchProductBrands(),
        api.get<Unit[]>('/units'),
        api.get<GeneralSettings>('/generalsettings')
      ]);
      
      console.log('📦 Units response:', unitsRes.data);
      console.log('📦 Units count:', unitsRes.data.length);
      
      // Kategorilere hasChildren özelliği ekle
      const categoriesWithChildren = categoriesRes.data.map((cat: Category) => ({
        ...cat,
        hasChildren: categoriesRes.data.some((child: Category) => child.parentId === cat.id)
      }));
      
      setCategories(categoriesWithChildren);
      setProductBrands(Array.isArray(productBrandsRes) ? productBrandsRes : []);
      setUnits(unitsRes.data);
      setSettings(settingsRes.data);
      
      // Default kar oranlarını ayarla
      if (settingsRes.data.DefaultRetailMarginPercent) {
        setRetailMarginPercent(parseFloat(settingsRes.data.DefaultRetailMarginPercent));
      }
      if (settingsRes.data.DefaultWholesaleMarginPercent) {
        setWholesaleMarginPercent(parseFloat(settingsRes.data.DefaultWholesaleMarginPercent));
      }

      // Add modunda minimum gerekli alanlar için default değerler
      if (!isEdit) {
        // Backend için gerekli minimum alanlar
        const defaultTaxRate = settingsRes.data ? parseFloat(settingsRes.data.DefaultTaxRate || '20') : 20;
        
        // TEMEL BİRİM olan Unit'i bul ve onun UnitSetId'sini kullan
        let defaultUnitId = 1;
        let defaultUnitSetId = 1;
        
        if (unitsRes.data.length > 0) {
          console.log('🔍 Searching for base unit...');
          console.log('All units:', JSON.stringify(unitsRes.data, null, 2));
          
          // Temel birim olan Unit'i bul (isBaseUnit = true veya baseRatio = 1)
          const baseUnit = unitsRes.data.find((u: any) => {
            console.log(`Checking unit ${u.id}:`, {
              isBaseUnit: u.isBaseUnit,
              IsBaseUnit: u.IsBaseUnit,
              baseRatio: u.baseRatio,
              BaseRatio: u.BaseRatio,
              conversionFactor: u.conversionFactor,
              ConversionFactor: u.ConversionFactor
            });
            return u.isBaseUnit === true || u.IsBaseUnit === true || 
                   u.baseRatio === 1 || u.BaseRatio === 1 ||
                   u.conversionFactor === 1 || u.ConversionFactor === 1;
          });
          
          if (baseUnit) {
            defaultUnitId = baseUnit.id;
            // Temel birimin UnitSetId'sini al
            defaultUnitSetId = (baseUnit as any).unitSetId || (baseUnit as any).UnitSetId || 1;
            console.log('✅ Found base unit:', baseUnit);
            console.log('Base Unit ID:', defaultUnitId);
            console.log('UnitSet ID from base unit:', defaultUnitSetId);
          } else {
            // Temel birim bulunamazsa ilk Unit'i kullan
            const firstUnit = unitsRes.data[0];
            defaultUnitId = firstUnit.id;
            defaultUnitSetId = (firstUnit as any).unitSetId || (firstUnit as any).UnitSetId || 1;
            console.warn('⚠️ No base unit found, using first unit:', firstUnit);
          }
        } else {
          console.error('❌ No units found in response!');
        }
        
        const defaultProductType = 1;
        setProductType(defaultProductType);

        form.setFieldsValue({
          // Backend gereksinimi için
          baseUnitId: defaultUnitId,
          unitSetId: defaultUnitSetId, // TEMEL BİRİMDEN gelen UnitSet ID'si
          taxRate: defaultTaxRate,
          isPurchasePrice: false,
          productType: defaultProductType,
          trackStock: determineTrackStockByType(defaultProductType),
          enableCriticalStockAlert: false,
          initialStock: 0,
          isActive: true
        });
      }
    } catch (error) {
      message.error('Veriler yüklenirken hata oluştu');
    }
  };

  // Ürünün varyant durumunu kontrol et
  const checkProductVariants = async (productId: string) => {
    try {
      const { data } = await api.get(`/productvariants/by-product/${productId}`);
      // Eğer ürünün varyantı varsa hasVariants = true
      setHasVariants(data && data.length > 0);
    } catch (error) {
      console.error('Varyant kontrolü sırasında hata:', error);
      setHasVariants(false);
    }
  };

  const loadProduct = async (productId: string) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/products/${productId}`);
      const productBrandIdRaw = data.productBrandId ?? data.ProductBrandId;
      const productBrandIdNormalized = productBrandIdRaw != null && productBrandIdRaw !== '' && Number(productBrandIdRaw) > 0
        ? Number(productBrandIdRaw)
        : undefined;
      form.setFieldsValue({
        ...data,
        productBrandId: productBrandIdNormalized});
      savedProductIsActiveRef.current = data.isActive !== undefined && data.isActive !== null ? !!data.isActive : true;
      const incomingProductType = data.productType ?? 1;
      setProductType(incomingProductType);
      form.setFieldsValue({
        productType: incomingProductType,
        trackStock: determineTrackStockByType(incomingProductType)});
      
      // Fiyatlandırma ayarlarını state'e set et
      setIsPriceVariant(data.isPriceVariant || false);
      setIsWholesalePrice(data.isWholesalePrice || false);
      
      // Otomatik kar oranı ayarlarını state'e set et
      setUseAutoMargin(data.useAutoMargin !== undefined ? data.useAutoMargin : true);
      setRetailMarginPercent(data.retailMarginPercent || null);
      setWholesaleMarginPercent(data.wholesaleMarginPercent || null);
      
      // Stok hareketi var mı kontrol et
      setHasStockHistory(data.hasStockHistory || false);
      
      // Eğer stok hareketi varsa InitialStock'u form'dan kaldır
      if (data.hasStockHistory) {
        form.setFieldsValue({ initialStock: undefined });
      }
      
      // Eğer geçici kod varsa (TEMP veya NEW içeriyorsa), otomatik modu aktif bırak (add'den gelince de TEMP ile kaydedilir)
      const isTempCode = data.productCode && (
        data.productCode === 'TEMP' ||
        data.productCode.includes('TEMP-') || 
        data.productCode.includes('-NEW-')
      );
      
      if (isTempCode) {
        setAutoGenerateCode(true);
        // Hemen yeni kodu üret
        setTimeout(() => {
          const newCode = generateProductCode();
          form.setFieldsValue({ productCode: newCode });
        }, 100);
      } else if (data.productCode) {
        setAutoGenerateCode(false);
      }
      
      // Ürünün varyant durumunu kontrol et
      await checkProductVariants(productId);
      
      // Depo ve stok bilgilerini yükle
      await loadWarehousesAndStocks(productId);

      // Kategori öznitelikleri ve değerleri (genişlik, yükseklik, üretim yılı vb.)
      if (data.categoryId) {
        skipAttributeResetRef.current = true;
        const attrs = await fetchCategoryAttributes(data.categoryId);
        setCategoryAttributes(attrs);
        const fromApi = (data.attributeValues || []) as ProductAttributeValueDto[];
        setProductAttributeValues(
          attrs.map(a => {
            const av = fromApi.find(x => x.categoryAttributeId === a.id);
            if (!av) return { categoryAttributeId: a.id };
            return {
              categoryAttributeId: a.id,
              valueNumeric: av.valueNumeric,
              valueText: av.valueText ?? undefined,
              valueDate: av.valueDate ?? undefined};
          })
        );
      }
    } catch (error) {
      message.error('Ürün yüklenirken hata oluştu');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  // Depo ve stok bilgilerini yükle
  const loadWarehousesAndStocks = async (productId: string) => {
    try {
      setStocksLoading(true);
      const [warehousesRes, stocksRes] = await Promise.all([
        api.get<Warehouse[]>('/warehouses'),
        api.get(`/stock/by-product/${productId}`)
      ]);

      const allWarehouses = warehousesRes.data.filter((w: any) => w.isActive);
      setWarehouses(allWarehouses);

      const existingStocks = stocksRes.data;

      // Her depo için WarehouseStock objesi oluştur
      const warehouseStockData: WarehouseStock[] = allWarehouses.map((wh: Warehouse) => {
        const stock = existingStocks.find((s: any) => s.warehouseId === wh.id);
        return {
          stockId: stock?.id,
          warehouseId: wh.id,
          warehouseName: wh.warehouseName,
          quantity: stock?.quantity ?? null,
          minStockLevel: stock?.minStockLevel ?? 0,
          maxStockLevel: stock?.maxStockLevel ?? 1000,
          shelfCode: stock?.shelfCode ?? undefined,
          hasStockRecord: !!stock
        };
      });

      setWarehouseStocks(warehouseStockData);
    } catch (error) {
      console.error('Depo stok bilgileri yüklenirken hata:', error);
    } finally {
      setStocksLoading(false);
    }
  };

  // Toplu min/max uygula
  const handleBulkApply = () => {
    const updated = warehouseStocks.map(ws => ({
      ...ws,
      minStockLevel: bulkMin,
      maxStockLevel: bulkMax
    }));
    setWarehouseStocks(updated);
    message.success('Tüm depolara uygulandı');
  };

  // Stok ayarlarını kaydet
  const handleSaveStockSettings = async () => {
    try {
      setStocksLoading(true);
      
      for (const ws of warehouseStocks) {
        if (ws.stockId) {
          // Mevcut kayıt, güncelle
          await api.patch(`/stock/${ws.stockId}/settings`, {
            minStockLevel: ws.minStockLevel,
            maxStockLevel: ws.maxStockLevel,
            shelfCode: ws.shelfCode || null
          });
        } else {
          // Yeni kayıt oluştur (normalde ürün oluşturulurken otomatik oluşuyor ama yine de)
          await api.post('/stock/create-initial', {
            productId: parseInt(id!),
            productVariantId: null,
            warehouseId: ws.warehouseId,
            minStockLevel: ws.minStockLevel,
            maxStockLevel: ws.maxStockLevel,
            shelfCode: ws.shelfCode || null
          });
        }
      }
      
      message.success('Stok ayarları kaydedildi');
      // Yeniden yükle
      await loadWarehousesAndStocks(id!);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Stok ayarları kaydedilirken hata oluştu');
    } finally {
      setStocksLoading(false);
    }
  };

  // Tek bir depo için min/max/raf kodu değişikliği
  const handleStockChange = (warehouseId: number, field: 'min' | 'max' | 'shelfCode', value: number | string | null) => {
    setWarehouseStocks(prev =>
      prev.map(ws =>
        ws.warehouseId === warehouseId
          ? {
              ...ws,
              minStockLevel: field === 'min' ? (value as number ?? 0) : ws.minStockLevel,
              maxStockLevel: field === 'max' ? (value as number ?? 0) : ws.maxStockLevel,
              shelfCode: field === 'shelfCode' ? (value as string ?? undefined) : ws.shelfCode
            }
          : ws
      )
    );
  };

  // Kategori hiyerarşisinden kısa kodlar al
  const getCategoryHierarchyCodes = (categoryId: number): string => {
    const codes: string[] = [];
    let currentId: number | null | undefined = categoryId;

    while (currentId) {
      const category = categories.find(c => c.id === currentId);
      if (!category) break;
      // Kategori kodunu en fazla 3 karakter al (büyük harflerle)
      const code = category.categoryCode?.substring(0, 3).toUpperCase() || 'XXX';
      codes.unshift(code);
      currentId = category.parentId;
    }

    return codes.join('-');
  };

  // Otomatik ürün kodu üret: Hiyerarşi-ÜrünID
  // Örnek: ELK-TV-LED-18
  const generateProductCode = () => {
    const categoryId = form.getFieldValue('categoryId');
    if (!categoryId) return 'TEMP-NEW';
    
    const hierarchyCodes = getCategoryHierarchyCodes(categoryId);
    const productIdPart = id || 'NEW';
    
    // Format: ELK-TV-LED-18 (Hiyerarşi-ÜrünID)
    return `${hierarchyCodes}-${productIdPart}`;
  };

  // Kategori veya varyant değiştiğinde otomatik kod güncelle
  useEffect(() => {
    if (isEdit && autoGenerateCode) {
      const newCode = generateProductCode();
      form.setFieldsValue({ productCode: newCode });
    }
  }, [categories, productVariants, autoGenerateCode, id, isEdit]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      console.log('Form values before submit:', values);
      
      if (isEdit) {
        // Otomatik kod üret seçiliyse, kodu üret
        let finalValues = { ...values };
        if (autoGenerateCode) {
          finalValues.productCode = generateProductCode();
        }
        
        // Otomatik kar oranı değerlerini ekle
        finalValues.useAutoMargin = useAutoMargin;
        finalValues.retailMarginPercent = retailMarginPercent;
        finalValues.wholesaleMarginPercent = wholesaleMarginPercent;
      finalValues.productType = productType;
      finalValues.trackStock = determineTrackStockByType(productType);
        if (finalValues.isActive === undefined || finalValues.isActive === null) {
          finalValues.isActive = savedProductIsActiveRef.current;
        }
        
        console.log('Updating product with:', finalValues);
        const attributeValues = productAttributeValues
          .filter(p => p.valueNumeric != null || (p.valueText != null && p.valueText.trim() !== '') || p.valueDate != null)
          .map(p => ({ categoryAttributeId: p.categoryAttributeId, valueNumeric: p.valueNumeric, valueText: p.valueText || undefined, valueDate: p.valueDate || undefined }));
        const sentBrandId = (finalValues.productBrandId != null && finalValues.productBrandId !== '' && Number(finalValues.productBrandId) > 0) ? Number(finalValues.productBrandId) : null;
        const updatePayload = {
          id: parseInt(id!),
          ...finalValues,
          productBrandId: sentBrandId,
          attributeValues: attributeValues.length ? attributeValues : undefined};
        await api.put(`/products/${id}`, updatePayload);
        const nextBrandId = sentBrandId != null ? sentBrandId : undefined;
        form.setFieldsValue({ productBrandId: nextBrandId });
        setProductBrandSelectKey((k) => k + 1);
        message.success('Ürün başarıyla güncellendi');
      } else {
        // Yeni ürün oluştur - Otomatik seçiliyse TEMP (edit'te üretilecek), manuel seçiliyse kullanıcı kodunu gönder
        const attributeValues = productAttributeValues
          .filter(p => p.valueNumeric != null || (p.valueText != null && p.valueText.trim() !== '') || p.valueDate != null)
          .map(p => ({ categoryAttributeId: p.categoryAttributeId, valueNumeric: p.valueNumeric, valueText: p.valueText || undefined, valueDate: p.valueDate || undefined }));
        const payload: any = {
          ...values,
          productCode: autoGenerateCode ? 'TEMP' : (values.productCode?.trim() || 'TEMP'),
          useAutoMargin: useAutoMargin,
          retailMarginPercent: retailMarginPercent,
          wholesaleMarginPercent: wholesaleMarginPercent,
          productType: productType,
          trackStock: determineTrackStockByType(productType),
          attributeValues: attributeValues.length ? attributeValues : undefined};
        if (!payload.initialStock || payload.initialStock === 0) {
          delete payload.initialStock;
        }
        
        console.log('Creating product with payload:', payload);
        
        const { data } = await api.post('/products', payload);
        message.success('Ürün başarıyla oluşturuldu. Diğer bilgileri ekleyebilirsiniz.');
        navigate(`/products/edit/${data.id}`);
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      console.error('Error response:', error.response?.data);
      message.error(error.response?.data?.message || `Ürün ${isEdit ? 'güncellenirken' : 'oluşturulurken'} hata oluştu`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/products');
  };

  // Kategori yolunu oluştur (Ana > Alt > Seçilen)
  const getCategoryPath = (categoryId: number): string => {
    const path: string[] = [];
    let currentId: number | null | undefined = categoryId;

    while (currentId) {
      const category = categories.find(c => c.id === currentId);
      if (!category) break;
      path.unshift(category.categoryName);
      currentId = category.parentId;
    }

    return path.join(' > ');
  };

  // Kategorileri hiyerarşik sırala
  const sortCategoriesHierarchically = (cats: Category[]): Category[] => {
    const result: Category[] = [];
    
    const addCategoryAndChildren = (categoryId: number, level: number = 0) => {
      const category = cats.find(c => c.id === categoryId);
      if (!category) return;
      
      result.push({ ...category, level } as any);
      
      const children = cats.filter(c => c.parentId === categoryId);
      children.forEach(child => addCategoryAndChildren(child.id, level + 1));
    };
    
    const rootCategories = cats.filter(cat => !cat.parentId);
    rootCategories.forEach(root => addCategoryAndChildren(root.id, 0));
    
    return result;
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleCancel} type="text" />
            <span>{isEdit ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</span>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setSearchParams({ tab: key });
            }}
            items={[
              // Temel Bilgiler - Her zaman görünür
              {
                key: 'basic',
                label: (
                  <span>
                    <AppstoreOutlined /> Temel Bilgiler
                  </span>
                ),
                children: (
                  <Card title="Temel Bilgiler" size="small" style={{ marginBottom: 16 }}>
            {/* Ürün Kodu, Kategori, Birim - Add ve Edit'te */}
            <Row gutter={16}>
              {/* Ürün Kodu - Add ve Edit'te; default Otomatik, kullanıcı Manuel seçebilir */}
              <Col span={isEdit ? 6 : 8}>
                <Form.Item label="Ürün Kodu">
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item 
                      name="productCode" 
                      noStyle 
                      rules={[{ required: !autoGenerateCode, message: 'Ürün kodu gerekli' }]}
                    >
                      <Input 
                        placeholder={autoGenerateCode ? "Otomatik oluşturulacak" : "Ürün kodu giriniz"} 
                        disabled={autoGenerateCode}
                        style={{ width: '70%' }}
                      />
                    </Form.Item>
                    <Button 
                      type={autoGenerateCode ? 'primary' : 'default'}
                      onClick={() => setAutoGenerateCode(!autoGenerateCode)}
                      style={{ width: '30%' }}
                    >
                      {autoGenerateCode ? '🤖 Otomatik' : '✏️ Manuel'}
                    </Button>
                  </Space.Compact>
                </Form.Item>
              </Col>

              {/* Kategori - Edit'te 1/4, Add'de 1/3 */}
              <Col span={isEdit ? 6 : 8}>
                <Form.Item name="categoryId" label="Kategori" rules={[{ required: true, message: 'Kategori seçiniz' }]}>
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="categoryId" noStyle rules={[{ required: true, message: 'Kategori seçiniz' }]}>
                      <Select 
                        placeholder="Kategori seçiniz" 
                        showSearch
                        filterOption={(input, option) => {
                          const searchText = `${option?.children}`.toLowerCase();
                          return searchText.includes(input.toLowerCase());
                        }}
                        optionLabelProp="label"
                        style={{ width: '85%' }}
                      >
                        {sortCategoriesHierarchically(categories).map((cat: any) => {
                          const level = cat.level || 0;
                          const indent = '　'.repeat(level);
                          const prefix = level > 0 ? '├─ ' : '📁 ';
                          const fullPath = getCategoryPath(cat.id);
                          const hasChildren = cat.hasChildren || false;
                          
                          return (
                            <Select.Option 
                              key={cat.id} 
                              value={cat.id}
                              label={fullPath}
                              disabled={hasChildren}
                            >
                              <span style={{ fontFamily: 'monospace' }}>
                                {indent}{prefix}{cat.categoryName}
                              </span>
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                    <Button 
                      type="dashed"
                      onClick={() => window.open('/categories', '_blank')}
                      style={{ width: '15%' }}
                      title="Yeni kategori ekle"
                    >
                      ➕
                    </Button>
                  </Space.Compact>
                </Form.Item>
              </Col>

              {/* Birim - Edit'te 1/4, Add'de 1/3 */}
              <Col span={isEdit ? 6 : 8}>
                  <Form.Item name="baseUnitId" label="Varsayılan Birim" rules={[{ required: true, message: 'Birim seçiniz' }]} tooltip="Ürün için kullanılacak varsayılan birim">
                    <Select 
                      placeholder="Birim seçiniz" 
                      showSearch 
                      optionFilterProp="children"
                      onChange={(unitId) => {
                        // Seçilen Unit'in UnitSetId'sini değil, TEMEL BİRİMİN UnitSetId'sini set et
                        const selectedUnit = units.find((u: any) => u.id === unitId);
                        if (selectedUnit) {
                          // Seçilen Unit'in UnitSetId'sine sahip TEMEL BİRİMİ bul
                          const selectedUnitSetId = (selectedUnit as any).unitSetId || (selectedUnit as any).UnitSetId;
                          const baseUnitInSet = units.find((u: any) => 
                            ((u as any).unitSetId === selectedUnitSetId || (u as any).UnitSetId === selectedUnitSetId) &&
                            (u.isBaseUnit === true || (u as any).IsBaseUnit === true || (u as any).baseRatio === 1 || (u as any).BaseRatio === 1 || (u as any).conversionFactor === 1)
                          );
                          
                          if (baseUnitInSet) {
                            const unitSetIdToUse = (baseUnitInSet as any).unitSetId || (baseUnitInSet as any).UnitSetId;
                            console.log('Selected Unit:', selectedUnit);
                            console.log('Base Unit in same set:', baseUnitInSet);
                            console.log('Auto-setting UnitSetId from base unit:', unitSetIdToUse);
                            form.setFieldsValue({ unitSetId: unitSetIdToUse });
                          }
                        }
                      }}
                    >
                      {units.map(unit => {
                        const unitName = unit.name || unit.unitName || 'Birim';
                        const unitSymbol = unit.symbol || unit.unitCode || '';
                        return (
                          <Select.Option key={unit.id} value={unit.id}>
                            {unitName} ({unitSymbol})
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
              </Col>

               <Col span={isEdit ? 6 : 8}>
               <Form.Item 
                      name="productType" 
                      label="Ürün Tipi" 
                      rules={[{ required: true, message: 'Ürün tipi seçiniz' }]}
                      tooltip="Ürün tipi stok takibi ve diğer süreçleri belirler"
                    >
                      <Select
                        placeholder="Ürün tipi seçiniz"
                        onChange={(value: number) => {
                          setProductType(value);
                          form.setFieldsValue({
                            trackStock: determineTrackStockByType(value)});
                        }}
                      >
                        {PRODUCT_TYPE_OPTIONS.map(option => (
                          <Select.Option key={option.value} value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
              </Col>

            </Row>

            {/* Ürün markası (Bridgestone, Pirelli vb.) - opsiyonel */}
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Ürün markası">
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="productBrandId" noStyle>
                      <Select
                        key={productBrandSelectKey}
                        placeholder="Marka seçiniz (opsiyonel)"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label ?? '')
                            .toLocaleLowerCase('tr-TR')
                            .includes(input.toLocaleLowerCase('tr-TR'))
                        }
                        options={productBrands.map(b => ({
                          value: b.id,
                          label: b.isFavourite ? `⭐ ${b.name}` : b.name}))}
                        style={{ width: '85%' }}
                      />
                    </Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => window.open('/product-brands', '_blank')}
                      style={{ width: '15%' }}
                      title="Yeni ürün markası ekle"
                    >
                      ➕
                    </Button>
                  </Space.Compact>
                </Form.Item>
              </Col>
            </Row>

            {/* Kategori öznitelikleri (genişlik, yükseklik, üretim yılı vb.) - kategoride tanımlıysa göster */}
            {categoryAttributes.length > 0 && (
              <Card size="small" title="Kategori öznitelikleri" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  {categoryAttributes.map((attr, idx) => {
                    const val = productAttributeValues[idx];
                    if (!val || val.categoryAttributeId !== attr.id) return null;
                    const updateVal = (updates: Partial<typeof val>) => {
                      setProductAttributeValues(prev => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], ...updates };
                        return next;
                      });
                    };
                    const label = attr.unit ? `${attr.name} (${attr.unit})` : attr.name;
                    if (attr.dataType === AttributeDataType.Number) {
                      return (
                        <Col key={attr.id} span={8}>
                          <Form.Item label={label}>
                            <InputNumber
                              style={{ width: '100%' }}
                              value={val.valueNumeric}
                              onChange={v => updateVal({ valueNumeric: v ?? undefined })}
                              placeholder={attr.name}
                            />
                          </Form.Item>
                        </Col>
                      );
                    }
                    if (attr.dataType === AttributeDataType.Date) {
                      return (
                        <Col key={attr.id} span={8}>
                          <Form.Item label={label}>
                            <DatePicker
                              style={{ width: '100%' }}
                              value={val.valueDate ? dayjs(val.valueDate) : null}
                              onChange={(d) => updateVal({ valueDate: d ? d.toISOString() : undefined })}
                              format="DD.MM.YYYY"
                            />
                          </Form.Item>
                        </Col>
                      );
                    }
                    return (
                      <Col key={attr.id} span={8}>
                        <Form.Item label={label}>
                          <Input
                            value={val.valueText ?? ''}
                            onChange={e => updateVal({ valueText: e.target.value || undefined })}
                            placeholder={attr.name}
                          />
                        </Form.Item>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            )}
            
            {/* Ürün Açıklama ve Fiyatlandırma Ayarları yan yana (Edit modunda) */}
            {isEdit ? (
              <Row gutter={16}>
 
                {/* Ürün Açıklama - Sol taraf 1/2 */}
                <Col span={12}>
                <Form.Item name="productName" label="Ürün Adı" rules={[{ required: true, message: 'Ürün adı gerekli' }]}>
                  <Input placeholder="Ürün adı giriniz" />
                </Form.Item>
                  <Form.Item name="description" label="Ürün Açıklama Metni">
                    <Input.TextArea rows={8} placeholder="Ürün açıklaması" />
                  </Form.Item>
                </Col>

                {/* Fiyatlandırma Ayarları - Sağ taraf 1/2 */}
                <Col span={12}>
                <div style={{   marginBottom: 8, fontSize: '14px' }}>Fiyatlandırma Ayarları</div>
                  <div 
                    style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px', paddingBottom: '24px',paddingTop: '24px' }}
                    className="pricing-settings-compact"
                  >
                
                    <style>{`
                      .pricing-settings-compact .ant-form-item {
                        margin-bottom: 2px !important;
                      }
                      .pricing-settings-compact .ant-form-item-label > label {
                        font-size: 13px !important;
                      }
                    `}</style>
                <Row gutter={[8, 8]}>
                  
                  <Col span={5}>
                    <Form.Item 
                      name="isPriceVariant" 
                      label="Varyant Var mı" 
                      valuePropName="checked"
                      tooltip="Aktif olursa her varyant için ayrı fiyat girilir. Pasif olursa genel ürün fiyatı kullanılır."
                    >
                      <Switch 
                        checkedChildren="Aktif" 
                        unCheckedChildren="Pasif"
                        onChange={async (checked) => {
                          form.setFieldsValue({ isPriceVariant: checked });
                          
                          // ✨ IsPriceVariant true yapıldığında ana ürün stokunu kontrol et
                          if (checked && isEdit && id) {
                            try {
                              // Ana ürün stokunu kontrol et
                              const stockResponse = await api.get(`/products/${id}/stock`);
                              const mainProductStock = stockResponse.data?.totalStock || 0;
                              
                              // Ana ürün stoku varsa modal göster
                              if (mainProductStock > 0) {
                                // Varyantları yükle (henüz oluşturulmamış olabilir)
                                try {
                                  const variantsResponse = await api.get(`/productvariants/by-product/${id}`);
                                  setAvailableVariants(variantsResponse.data || []);
                                } catch (error) {
                                  console.error('Varyant yükleme hatası:', error);
                                  setAvailableVariants([]);
                                }
                                
                                setStockTransferData({
                                  mainProductStock: mainProductStock,
                                  message: `Ana ürün stokunda ${mainProductStock} adet stok bulundu. Hangi varyanta aktarmak istersiniz?`
                                });
                                setStockTransferModalVisible(true);
                                setSelectedTargetVariantId(null);
                              }
                            } catch (error) {
                              console.error('Stok kontrolü hatası:', error);
                              // Hata olsa bile switch'i aç
                            }
                          }
                          
                          setIsPriceVariant(checked); // State'i de güncelle
                          if (id) {
                            try {
                              await api.patch(`/productsettings/${id}/price-variant`, checked);
                              message.success('Fiyatlandırma ayarı güncellendi');
                            } catch (error: any) {
                              console.error('Update error:', error.response?.data);
                              message.error('Ayar güncellenirken hata oluştu');
                            }
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item 
                      name="isWholesalePrice" 
                      label="Toptan Fiyat" 
                      valuePropName="checked"
                      tooltip="Aktif olursa toptan fiyat alanları görünür"
                    >
                      <Switch 
                        checkedChildren="Aktif" 
                        unCheckedChildren="Pasif"
                        onChange={async (checked) => {
                          form.setFieldsValue({ isWholesalePrice: checked });
                          setIsWholesalePrice(checked); // State'i de güncelle
                          if (id) {
                            try {
                              await api.patch(`/productsettings/${id}/wholesale-price`, checked);
                              message.success('Toptan fiyat ayarı güncellendi');
                            } catch (error: any) {
                              console.error('Update error:', error.response?.data);
                              message.error('Ayar güncellenirken hata oluştu');
                            }
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item 
                      name="enableCriticalStockAlert" 
                      label="Kritik Stok Uyarısı" 
                      valuePropName="checked"
                      tooltip="Toplam stok kritik seviyenin altına düştüğünde uyarı verilir"
                    >
                      <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                    </Form.Item>
                  </Col>
                  {enableCriticalStockAlert && (
                    <Col span={6}>
                      <Form.Item 
                        name="criticalStockLevel" 
                        label="Kritik Stok Seviyesi"
                        tooltip="Tüm depolar toplamı bu seviyenin altına düştüğünde uyarı"
                      >
                        <InputNumber 
                          min={0} 
                          placeholder="Örn: 50"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
                
                {/* Otomatik Kar Oranı Ayarları */}
                <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                  
                  <Col span={8}>
                    <Form.Item 
                      label=" Otomatik Kar Oranı" 
                      tooltip="Mal alış fişi oluştururken alış fiyatına göre perakende ve toptan fiyatları otomatik hesapla"
                    >
                      <Switch 
                        checked={useAutoMargin}
                        checkedChildren="Aktif" 
                        unCheckedChildren="Pasif"
                        onChange={(checked) => {
                          setUseAutoMargin(checked);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  {useAutoMargin && (
                    <>
                      <Col span={8}>
                        <Form.Item 
                          label="Satış Kar Oranı (%)"
                          tooltip="Satış fiyatı = Alış Fiyatı × (1 + Kar Oranı / 100)"
                        >
                          <InputNumber 
                            value={retailMarginPercent}
                            min={0} 
                            max={1000}
                            placeholder="Örn: 50"
                            style={{ width: '100%' }}
                            onChange={(value) => setRetailMarginPercent(value)}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item 
                          label="Toptan Kar Oranı (%)"
                          tooltip="Toptan fiyat = Alış Fiyatı × (1 + Kar Oranı / 100)"
                        >
                          <InputNumber 
                            value={wholesaleMarginPercent}
                            min={0} 
                            max={1000}
                            placeholder="Örn: 30"
                            style={{ width: '100%' }}
                            onChange={(value) => setWholesaleMarginPercent(value)}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
                
                <Row gutter={[8, 8]}>
                
                 
                  <Col span={8}>
                    <Form.Item 
                      name="gtipCode" 
                      label="GTIP Kodu" 
                      tooltip="Gümrük Tarife İstatistik Pozisyonu"
                    >
                      <Input placeholder="GTIP kodu giriniz" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item 
                      name="taxRate" 
                      label="KDV Oranı (%)" 
                      rules={[{ required: true, message: 'KDV oranı gerekli' }]}
                    >
                      <InputNumber style={{ width: '100%' }} min={0} max={100} placeholder="KDV oranı" />
                    </Form.Item>
                  </Col>
                </Row>
                  </div>
                </Col>
              </Row>
            ) : (
              // Add modunda ürün adı ve açıklama
              <>
                <Form.Item name="productName" label="Ürün Adı" rules={[{ required: true, message: 'Ürün adı gerekli' }]}>
                  <Input placeholder="Ürün adı giriniz" />
                </Form.Item>
                <Form.Item name="description" label="Ürün Açıklama Metni">
                  <Input.TextArea rows={4} placeholder="Ürün açıklaması (opsiyonel)" />
                </Form.Item>
              </>
            )}

            <Form.Item name="isPurchasePrice" hidden>
              <Input type="hidden" />
            </Form.Item>

            {/* Hidden UnitSetId field - Backend için gerekli */}
            <Form.Item name="unitSetId" hidden>
              <Input type="hidden" />
            </Form.Item>
                  </Card>
                )},
              // Fiyatlandırma - Sadece Edit'te ve isPriceVariant false ise
              ...(isEdit && !isPriceVariant ? [{
                key: 'pricing',
                label: (
                  <span>
                    <DollarOutlined /> Fiyatlandırma
                  </span>
                ),
                children: (
                  <div style={{ marginBottom: 16 }}>
                    <SimplePriceManager
                      productId={id ? parseInt(id) : undefined}
                      isWholesalePrice={isWholesalePrice}
                      isPurchasePrice={false}
                    />
                  </div>
                )}] : []),
              // Varyantlar - Sadece Edit'te ve isPriceVariant true ise
              ...(isEdit && isPriceVariant ? [{
                key: 'variants',
                label: (
                  <span>
                    <UnorderedListOutlined /> Varyantlar
                  </span>
                ),
                children: (
                  <div style={{ marginBottom: 16 }}>
                    <ProductVariantSelector
                      productId={id ? parseInt(id) : undefined}
                      productCode={form.getFieldValue('productCode')}
                      isPriceVariant={isPriceVariant}
                      isWholesalePrice={isWholesalePrice}
                      isPurchasePrice={false}
                    />
                  </div>
                )}] : []),
              // Barkodlar - Sadece Edit'te ve !hasVariants ise
              ...(isEdit && !hasVariants ? [{
                key: 'barcodes',
                label: (
                  <span>
                    <BarcodeOutlined /> Barkodlar
                  </span>
                ),
                children: (
                  <div style={{ marginBottom: 16 }}>
                    <ProductBarcodeManager
                      productId={id ? parseInt(id) : undefined}
                      onChange={setProductBarcodes}
                      showBulkGenerate={false}
                    />
                  </div>
                )}] : []),
              // Görseller - Sadece Edit'te
              ...(isEdit ? [{
                key: 'images',
                label: (
                  <span>
                    <PictureOutlined /> Görseller
                  </span>
                ),
                children: (
                  <div style={{ marginBottom: 16 }}>
                    <ProductImageUploader productId={id ? parseInt(id) : undefined} />
                  </div>
                )}] : []),
              // Kanallar - Sadece Edit'te
              ...(isEdit ? [{
                key: 'channels',
                label: (
                  <span>
                    <ShopOutlined /> Kanallar
                  </span>
                ),
                children: (
                  <div style={{ marginBottom: 16 }}>
                    <ProductChannelManager
                      productId={id ? parseInt(id) : undefined}
                      hasVariants={hasVariants}
                      isPriceVariant={isPriceVariant}
                    />
                  </div>
                )}] : []),
              // Uyumluluk - Sadece Edit'te
              ...(isEdit && id ? [{
                key: 'compatibility',
                label: (
                  <span>
                    <CarOutlined /> Uyumluluk
                  </span>
                ),
                children: (
                  <div style={{ marginBottom: 16 }}>
                    <VehicleCompatibilityManager
                      productId={parseInt(id)}
                      productName={form.getFieldValue('productName')}
                    />
                  </div>
                )}] : []),
              // Stok - Sadece Edit'te
              ...(isEdit ? [{
                key: 'stock',
                label: (
                  <span>
                    <HomeOutlined /> Stok
                  </span>
                ),
                children: (
                  <Card 
                    title="Depo Stok Ayarları" 
                    size="small" 
                    style={{ marginBottom: 16 }}
                    extra={
                      <Button 
                        type="primary" 
                        size="small"
                        onClick={handleSaveStockSettings}
                        loading={stocksLoading}
                      >
                        Kaydet
                      </Button>
                    }
                  >
                    <Table
                      dataSource={warehouseStocks}
                      rowKey="warehouseId"
                      pagination={false}
                      size="small"
                      loading={stocksLoading}
                      columns={[
                        {
                          title: 'Depo',
                          dataIndex: 'warehouseName',
                          key: 'warehouseName',
                          width: 200},
                        {
                          title: 'Mevcut Stok',
                          dataIndex: 'quantity',
                          key: 'quantity',
                          width: 120,
                          render: (quantity) => quantity !== null ? quantity.toFixed(2) : '-',
                          align: 'right' as const},
                        {
                          title: 'Min Stok',
                          key: 'minStockLevel',
                          width: 120,
                          render: (_, record) => (
                            <InputNumber
                              value={record.minStockLevel}
                              onChange={(value) => handleStockChange(record.warehouseId, 'min', value)}
                              style={{ width: '100%' }}
                              size="small"
                              min={0}
                              step={0.01}
                              precision={2}
                            />
                          ),
                          align: 'right' as const},
                        {
                          title: 'Max Stok',
                          key: 'maxStockLevel',
                          width: 120,
                          render: (_, record) => (
                            <InputNumber
                              value={record.maxStockLevel}
                              onChange={(value) => handleStockChange(record.warehouseId, 'max', value)}
                              style={{ width: '100%' }}
                              size="small"
                              min={0}
                              step={0.01}
                              precision={2}
                            />
                          ),
                          align: 'right' as const},
                        {
                          title: 'Raf Kodu',
                          key: 'shelfCode',
                          width: 150,
                          render: (_, record) => (
                            <Input
                              value={record.shelfCode || ''}
                              onChange={(e) => handleStockChange(record.warehouseId, 'shelfCode', e.target.value)}
                              placeholder="Örn: A-12-3"
                              style={{ width: '100%' }}
                              size="small"
                            />
                          )},
                      ]}
                    />
                    <div style={{ marginTop: 16 }}>
                      <Space>
                        <InputNumber
                          placeholder="Toplu Min"
                          value={bulkMin}
                          onChange={(value) => setBulkMin(value ?? 0)}
                          size="small"
                          style={{ width: 120 }}
                          min={0}
                        />
                        <InputNumber
                          placeholder="Toplu Max"
                          value={bulkMax}
                          onChange={(value) => setBulkMax(value ?? 1000)}
                          size="small"
                          style={{ width: 120 }}
                          min={0}
                        />
                        <Button size="small" onClick={handleBulkApply}>
                          Toplu Uygula
                        </Button>
                      </Space>
                    </div>
                  </Card>
                )}] : []),
              // Ayarlar - Sadece Edit'te
              ...(isEdit ? [{
                key: 'settings',
                label: (
                  <span>
                    <SettingOutlined /> Ayarlar
                  </span>
                ),
                children: (
                  <Card title="Ayarlar" size="small" style={{ marginBottom: 16 }}>
                    <Form.Item name="isActive" label="Durum" valuePropName="checked" initialValue={true}>
                      <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
                    </Form.Item>
                  </Card>
                )}] : []),
            ].filter(Boolean)}
          />

          {/* Submit Buttons */}
          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                {isEdit ? 'Güncelle' : 'Kaydet ve Devam Et'}
              </Button>
              <Button onClick={handleCancel}>
                {isEdit ? 'Listeye Dön' : 'İptal'}
              </Button>
            </Space>
          </Form.Item>
          </Form>
        </Card>

        {/* Stok Transfer Modal - Ana ürün stokunu varyanta aktar */}
        <Modal
          title="Stok Transferi"
          open={stockTransferModalVisible}
          onOk={async () => {
            if (!id) return;
            
            if (!selectedTargetVariantId) {
              message.warning('Lütfen bir varyant seçin');
              return;
            }

            try {
              setLoading(true);
              await api.post(`/productvariants/products/${id}/transfer-stock-to-variant`, {
                targetVariantId: selectedTargetVariantId
              });
              message.success('Stok başarıyla varyanta aktarıldı');
              setStockTransferModalVisible(false);
              setSelectedTargetVariantId(null);
              setStockTransferData(null);
              
              // Stok bilgilerini yeniden yükle
              if (id) {
                await loadWarehousesAndStocks(id);
              }
            } catch (error: any) {
              console.error('Stok transferi hatası:', error);
              message.error(error.response?.data?.message || 'Stok transferi sırasında hata oluştu');
            } finally {
              setLoading(false);
            }
          }}
          onCancel={() => {
            setStockTransferModalVisible(false);
            setSelectedTargetVariantId(null);
            setStockTransferData(null);
          }}
          okText="Aktar"
          cancelText="İptal"
        >
          <div>
            <p>{stockTransferData?.message || 'Ana ürün stokunu hangi varyanta aktarmak istersiniz?'}</p>
            {stockTransferData?.mainProductStock && (
              <Alert
                message={`Mevcut Stok: ${stockTransferData.mainProductStock} adet`}
                type="info"
                style={{ marginBottom: 16 }}
              />
            )}
            <Form.Item label="Hedef Varyant" required>
              <Select
                placeholder="Varyant seçin"
                value={selectedTargetVariantId}
                onChange={(value) => setSelectedTargetVariantId(value)}
                notFoundContent="Varyant bulunamadı"
              >
                {availableVariants.map((variant) => (
                  <Select.Option key={variant.id} value={variant.id}>
                    {variant.sku || variant.variantCombination || `Varyant ${variant.id}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {availableVariants.length === 0 && (
              <Alert
                message="Henüz varyant oluşturulmamış. Önce varyant oluşturun, sonra stok transferi yapabilirsiniz."
                type="warning"
              />
            )}
          </div>
        </Modal>
      </div>
    );
  };
  
  export default AddProduct;
